// run-command-outcome-observability.ts
//
// Split out of run-command-cli-wiring.ts (phase 05, N6 vòng 2 line budget):
// all the operator-facing print logic for `avatar autopilot run`'s outcome —
// config_invalid/doctor_blocked/before_dispatch_blocked/gateBlocked reasons,
// paused/aborted hints, blocked-ticket verdicts, release-flow notes, and P25
// unenforced-G2-conditions warnings. Pure printing, no control flow — the
// wiring module still owns process.exit decisions and lock/interrupt setup.
import {
  readBlockedTicketVerdictLines,
  readInProgressTicketLines,
} from "./blocked-ticket-verdict-observability.js";
import { describeWorktreeDirty } from "./bootstrap-manifest-paths.js";
import { describeUnenforcedG2Conditions } from "./g2-unenforced-conditions-observability.js";
import { describeDeployTargetHint, readMissionDeployTarget } from "./mission-deploy-target.js";
import {
  describeReleaseFlowOutcome,
  isReleaseFlowSettled,
  missionStatusLabelForReleaseFlow,
} from "./release-flow-resume-observability.js";
import type { ReleaseConfig } from "./release-manifest-hash.js";
import { describeMissingReleaseSecretsHint } from "./release-secrets-stdin-missing-keys-hint.js";
import type { ReleaseSecretsStdin } from "./release-secrets-stdin-reader.js";
import { answerCommandLines } from "./run-command-pending-question-hint.js";
import type { RunMissionResult } from "./supervisor-mission-run.js";

export interface PrintRunOutcomeInput {
  missionId: string;
  controlRoot: string;
  result: RunMissionResult;
  log: { info: (msg: string) => void; error: (msg: string) => void };
  /** BUG-2: needed to read the in_progress ticket's binding lease (`readInProgressTicketLines`). Optional so existing call sites without it keep compiling — they just skip that one courtesy line. */
  missionWorkspace?: string;
  /** V2 §4.4: the global config path `run` loaded — named in the deploy hint. */
  globalConfigPath: string;
  /** Phase 04b (§4.2): `config.release` + stdin map — only for the missing-release-key hint. */
  release?: ReleaseConfig;
  releaseSecretsStdin?: ReleaseSecretsStdin;
}

// Phase 06 (SU §4, AC-10, TD1): quota/§18 guards that fire MID-RUN now exit
// the loop instead of blocking-and-retrying forever — the operator's next
// action is always "run again after this point in time", so both branches
// below print the exact rerun command alongside the reason.
const RERUN_HINT = "Chạy lại `avatar autopilot run` sau thời điểm đó.";

export async function printRunOutcome(input: PrintRunOutcomeInput): Promise<void> {
  const { missionId, controlRoot, result, log, missionWorkspace } = input;

  if (result.outcome === "config_invalid") {
    log.error(
      `config_invalid: ${result.configError ?? "(không rõ lý do)"} — kiểm 'avatar autopilot doctor'.`,
    );
  } else if (result.outcome === "worktree_dirty") {
    log.error(describeWorktreeDirty(result.dirtyPaths ?? [])); // 06g R2a
  } else if (result.outcome === "doctor_blocked") {
    log.error("Doctor chặn run — kiểm 'avatar autopilot doctor' để biết chi tiết.");
    for (const failure of result.doctorFailures ?? []) log.error(`  ${failure}`);
  } else if (result.outcome === "before_dispatch_blocked") {
    // Req 6 (dogfood): same stdout shape as every other outcome — a session
    // blocked before dispatch used to print NOTHING on stdout at all.
    log.info(`Mission ${missionId}: outcome=before_dispatch_blocked sau ${result.ticksRun} tick.`);
    const reason = result.beforeDispatchReason ?? "(không rõ lý do)";
    // H6: the reason itself (quiet_hours) already spells out the resume
    // moment ("tạm dừng tới X.") — appending "— mở lại lúc X." on top printed
    // the same timestamp twice. Only append when `reason` does NOT already
    // carry it; a reason that omits resumeAt still gets the full hint.
    const hint =
      result.resumeAt && reason.includes(result.resumeAt)
        ? ` ${RERUN_HINT}`
        : result.resumeAt
          ? ` — mở lại lúc ${result.resumeAt}. ${RERUN_HINT}`
          : ` Xử lý lý do trên rồi chạy lại \`avatar autopilot run\`.`;
    log.error(`Dispatch bị chặn (§18): ${reason}${hint}`);
  } else if (result.outcome === "quota_sleeping") {
    log.error(
      `Mission ${missionId}: hết quota/rate-limit — tạm ngủ tới ${result.wakeAt ?? "(không rõ)"}. ${RERUN_HINT}`,
    );
  } else if (result.gateBlocked) {
    // Bug I (P15): actionable — names the ticket and the offending gate, and
    // the guard's own reason carries the `approve` command to run.
    log.error(
      `Dispatch bị chặn (gate §13): ticket ${result.gateBlocked.ticketId} — ${result.gateBlocked.reason}`,
    );
    // 06g R6a: `interview_unanswered` ⇒ the exact `answer` command per pending question.
    for (const line of await answerCommandLines(controlRoot, missionId, result.gateBlocked)) {
      log.error(line);
    }
  } else {
    // Review L5: `done` of the ticket loop is not the mission's last word when
    // a release flow is configured — name its state on the same stdout line.
    const release =
      result.outcome === "done" && result.releaseFlow !== undefined
        ? `, release=${missionStatusLabelForReleaseFlow(result.releaseFlow)}`
        : "";
    log.info(
      `Mission ${missionId}: outcome=${result.outcome}${release} sau ${result.ticksRun} tick.`,
    );
    // Re-review 2026-09-26 (A-c): `idle` because --max-ticks ran out, NOT
    // because the DAG is empty — the operator's next action is to run again.
    // Gated on `idle`: the interrupt override keeps the loop's fields but the
    // outcome is then `interrupted` (dogfood 260926 printed both hints).
    if (result.outcome === "idle" && result.tickBudgetExhausted) {
      const ids = result.pendingTicketIds ?? [];
      log.error(
        `Chạm --max-ticks (${result.ticksRun}) khi còn ${ids.length} ticket chưa xong (${ids.join(", ")}) — chạy lại \`avatar autopilot run\` để tiếp tục.`,
      );
    }
    // Re-review 2026-09-26 (A-F1): a cost/attempt ceiling breach is permanent
    // (WAITING_HUMAN) — name it and the human action, on startup AND mid-run.
    if (result.outcome === "blocked" && result.beforeDispatchReason) {
      log.error(
        `Dispatch bị chặn vĩnh viễn (§18, ${result.beforeDispatchCode ?? "trần"}): ${result.beforeDispatchReason} Mission chờ người (WAITING_HUMAN) — nâng trần trong ~/.avatar/autopilot-config.json rồi chạy lại \`avatar autopilot run\`.`,
      );
    }
    // Req 3 (R-15, C3): a `blocked` outcome caused by a stalled DAG (no gate,
    // no quota) — up to 5 human-readable reasons, stdout (same channel as the
    // `outcome=` line, D14).
    for (const line of result.stallReason ?? []) log.info(line);
    // Phase 04 (R-23, C3): the contract_error breaker halted this run —
    // stdout, same channel as `outcome=` and `stallReason` above.
    if (result.haltReason) log.info(`Ngắt mạch contract_error: ${result.haltReason}`);
    // Phase 04 (AC-01): paused/aborted are the two outcomes where the
    // operator's very next action is a specific command.
    if (result.outcome === "paused") {
      log.error("Mission đang pause — chạy `avatar autopilot resume` để tiếp tục.");
    }
    if (result.outcome === "aborted") {
      log.error("Mission đã ABORTED — vĩnh viễn, không dispatch thêm.");
    }
    // Phase 02 (D5, Req 8): `done` is the point the operator is most likely
    // to be finished with the mission — point straight at the cleanup command.
    // H14 (D15): only when the release flow is settled too — "Mission xong"
    // next to a pending G3 invited archiving a mission still waiting on it.
    // V2 §4.4 (E9): label `done` = no `config.release` (a configured release
    // labels released/done_without_deploy/…) — say how to deploy to the
    // declared target, BEFORE the archive hint. Label/exit code untouched.
    if (
      result.outcome === "done" &&
      missionStatusLabelForReleaseFlow(result.releaseFlow) === "done"
    ) {
      const deployHint = describeDeployTargetHint(
        await readMissionDeployTarget(controlRoot),
        input.globalConfigPath,
      );
      if (deployHint) log.info(deployHint);
    }
    if (result.outcome === "done" && isReleaseFlowSettled(result.releaseFlow)) {
      log.info(`Mission xong — dọn bằng: avatar autopilot archive --mission ${missionId}`);
    }
    // Plan 20260924-0150: why each ticket is blocked, keyed on ticket status
    // because a run can end `idle` with its only ticket blocked.
    for (const line of await readBlockedTicketVerdictLines(controlRoot)) log.error(line);
    // BUG-2 (dogfood 260925 luồng 4): a ticket stuck "in_progress" (orphaned
    // attempt) also stalls dispatch — never let `outcome=blocked` print with
    // NO explanation just because the stuck ticket's status isn't "blocked".
    if (missionWorkspace) {
      for (const line of await readInProgressTicketLines(controlRoot, missionWorkspace)) {
        log.error(line);
      }
    }
    // P24: ticket loop reaching "done" does NOT mean the release flow did.
    // Settled notes (released / done without deploy) are information, the
    // rest need the operator — stderr.
    const releaseNote = result.releaseFlow
      ? describeReleaseFlowOutcome(result.releaseFlow, missionId)
      : undefined;
    if (releaseNote && isReleaseFlowSettled(result.releaseFlow)) log.info(releaseNote);
    else if (releaseNote) log.error(releaseNote);
    // Phase 04b: a release key absent from every source ⇒ name it + the stdin command.
    const hint = await describeMissingReleaseSecretsHint({
      missionId,
      releaseFlow: result.releaseFlow,
      release: input.release,
      releaseSecretsStdin: input.releaseSecretsStdin,
    });
    if (hint) log.info(hint);
  }

  // P25: printed on EVERY outcome — a mission blocked on a gate is exactly
  // when the operator decides whether to approve, so an unmeasured §8
  // condition is most load-bearing there. Silent when nothing is unmeasured.
  const unenforcedNote = result.g2UnenforcedConditions
    ? describeUnenforcedG2Conditions(result.g2UnenforcedConditions)
    : undefined;
  if (unenforcedNote) log.error(unenforcedNote);
}

/**
 * Exit code of `avatar autopilot run`. H14 (D15): `done` alone is not enough
 * — a release flow that is still waiting (G3 pending, review report) or
 * blocked exits 1 like a pending G0–G2 does; only a settled flow exits 0.
 */
export function runCommandExitCode(result: RunMissionResult): number {
  if (result.outcome === "interrupted") return 130;
  if (result.outcome !== "done") return 1;
  return isReleaseFlowSettled(result.releaseFlow) ? 0 : 1;
}
